
import { Mixins, Component, Prop, Ref } from "vue-property-decorator";
import { SmartText } from "_components";
import { onResize, onScroll, onView } from "_mixins";
import { mapAnimation, lerp } from "_utils";

const EMPTY_DURATION = {
  seconds: 0,
  minutes: 0,
  hours: 0,
  days: 0,
};

@Component({
  name: "NextEventHero",
  components: { SmartText },
})
export default class NextEventHero extends Mixins(onResize, onScroll, onView) {
  @Prop({ type: String }) title: string;
  @Prop({ type: Boolean }) enableCountdown: boolean;
  @Prop({ type: String }) date: string;

  @Ref() wrapper: HTMLElement;
  @Ref() titleEl: SmartText;
  @Ref() pseudoTitleEl: SmartText;

  rootClass = "p-home-next-event-hero";

  duration = EMPTY_DURATION;
  interval;
  measurements = { start: 0, end: 0, amounts: [{ end: 0 }] };
  hasAnimation = false;
  targetCache = [];

  beforeMount() {
    this.setupInterval();
  }

  get outputDate() {
    if (this.duration !== EMPTY_DURATION) {
      const { seconds, minutes, hours, days } = this.duration;
      return `${this.formatDate(days)}:${this.formatDate(
        hours
      )}:${this.formatDate(minutes)}:${this.formatDate(seconds)}`;
    } else {
      return "00:00:00:00";
    }
  }

  formatDate(number: number) {
    return number.toLocaleString("en-US", {
      minimumIntegerDigits: 2,
      useGrouping: false,
    });
  }

  public calc() {
    this.resetElements();
    this.$nextTick(() => this.calcAnim());
  }

  setupInterval() {
    if (!this.interval) {
      const interval = () => {
        const start = new Date().getTime();
        const end = new Date(this.date.replace(/-/g, "/")).getTime();

        if (start < end) {
          let delta = Math.abs(end - start) / 1000;
          const days = Math.floor(delta / 86400);
          delta -= days * 86400;
          const hours = Math.floor(delta / 3600) % 24;
          delta -= hours * 3600;
          const minutes = Math.floor(delta / 60) % 60;
          delta -= minutes * 60;
          const seconds = Math.round(delta % 60);
          this.duration = {
            days,
            hours,
            minutes,
            seconds,
          };
        } else {
          this.duration = EMPTY_DURATION;
          this.clearInterval();
        }
        return interval;
      };
      this.interval = setInterval(interval(), 1000);
    }
  }

  onDestroy() {
    this.clearInterval();
  }

  clearInterval() {
    this.interval &&
      this.interval.clearInterval &&
      this.interval.clearInterval();
  }

  calcAnim() {
    const targets = this.titleEl.$el.children;
    const pseudoTargets = this.pseudoTitleEl.$el.children;
    if (targets.length > 0 && targets.length === pseudoTargets.length) {
      const title = this.titleEl.$el as HTMLElement;
      const titleY = title
        ? this.wrapper.parentElement.offsetTop + title.offsetTop
        : 0;

      this.measurements = {
        start: titleY ? titleY - window.innerHeight : 0,
        end: titleY ? titleY - 75 : 0,
        amounts: Array.from(targets).map((element: HTMLElement, index) => ({
          end:
            pseudoTargets[index].getBoundingClientRect().x -
            element.getBoundingClientRect().x,
        })),
      };
      this.hasAnimation = true;
    }
  }

  handleSplit() {
    this.calc();
  }

  onResize() {
    if (this.hasAnimation) this.calc();
  }

  resetElements() {
    if (this.titleEl) {
      const targets = this.titleEl.$el.children;
      Array.from(targets).map((element: HTMLElement) => {
        element.style.transform = null;
      });
    }
  }

  onScroll(scroll: number) {
    if (this.hasAnimation && this.titleEl.$el && this.isInView) {
      const { start, end, amounts } = this.measurements;
      if (scroll >= start && scroll <= end) {
        const targets = this.titleEl.$el.children;
        if (targets) {
          Array.from(targets).forEach((target: HTMLElement, index: number) => {
            const animTarget = mapAnimation(
              [this.measurements.start, end],
              [0, amounts[index].end],
              scroll
            );
            const cached = this.targetCache[index]
              ? this.targetCache[index]
              : 0;
            const lerped = lerp(cached, animTarget);
            target.style.transform = `translateX(${lerped}px) translateZ(0)`;
            this.targetCache[index] = lerped;
          });
        }
      }
    }
  }
}
